import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Trash2, Save, X, Edit } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const blankForm = () => ({
  code: '',
  discount_percent: 10,
  min_order_value: 0,
  start_date: '',
  expiry_date: '',
  active: true,
  banner_text: '',
  urgency_text: '',
});

export const CouponsManager = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCode, setEditingCode] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const r = await axios.get(`${API}/admin/coupons`, { headers: { Authorization: `Bearer ${token}` } });
      setCoupons(r.data || []);
    } catch (e) {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const startEdit = (c) => {
    setCreating(false);
    setEditingCode(c.code);
    setForm({
      code: c.code,
      discount_percent: c.discount_percent,
      min_order_value: c.min_order_value || 0,
      start_date: c.start_date ? c.start_date.slice(0, 10) : '',
      expiry_date: c.expiry_date ? c.expiry_date.slice(0, 10) : '',
      active: !!c.active,
      banner_text: c.banner_text || '',
      urgency_text: c.urgency_text || '',
    });
  };

  const cancelEdit = () => {
    setEditingCode(null);
    setCreating(false);
    setForm(blankForm());
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.code.trim()) { toast.error('Code is required'); return; }
    if (form.discount_percent < 1 || form.discount_percent > 100) { toast.error('Discount must be 1-100'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        discount_percent: parseInt(form.discount_percent, 10),
        min_order_value: parseInt(form.min_order_value || 0, 10),
        start_date: form.start_date || null,
        expiry_date: form.expiry_date || null,
      };
      if (creating) {
        await axios.post(`${API}/admin/coupons`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Coupon created');
      } else {
        await axios.put(`${API}/admin/coupons/${editingCode}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Coupon updated');
      }
      cancelEdit();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/coupons/${code}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Coupon deleted');
      fetchAll();
    } catch (e) {
      toast.error('Failed to delete coupon');
    }
  };

  return (
    <Card className="bg-white border-none shadow-lg p-6" data-testid="coupons-manager">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-seasons text-[#2C2C2C]">Coupons</h2>
          <p className="text-sm font-montserrat text-[#8B7D6B]">Manage discount codes, expiry and banner copy</p>
        </div>
        {!creating && !editingCode && (
          <Button
            onClick={() => { setCreating(true); setEditingCode(null); setForm(blankForm()); }}
            data-testid="new-coupon-btn"
            className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white"
          >
            <Plus className="h-4 w-4 mr-2" /> New Coupon
          </Button>
        )}
      </div>

      {(creating || editingCode) && (
        <Card className="bg-[#FAF6EE] border border-[#D4C4B0] p-4 mb-6" data-testid="coupon-form">
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Code">
              <Input value={form.code} onChange={(e) => set('code', e.target.value.toUpperCase())} placeholder="WELCOME10" data-testid="coupon-code-input" />
            </Field>
            <Field label="Discount %">
              <Input type="number" min={1} max={100} value={form.discount_percent} onChange={(e) => set('discount_percent', e.target.value)} data-testid="coupon-pct-input" />
            </Field>
            <Field label="Min Order ₹">
              <Input type="number" min={0} value={form.min_order_value} onChange={(e) => set('min_order_value', e.target.value)} data-testid="coupon-min-input" />
            </Field>
            <Field label="Start Date">
              <Input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} data-testid="coupon-start-input" />
            </Field>
            <Field label="Expiry Date">
              <Input type="date" value={form.expiry_date} onChange={(e) => set('expiry_date', e.target.value)} data-testid="coupon-expiry-input" />
            </Field>
            <Field label="Active">
              <label className="flex items-center gap-2 mt-2">
                <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} className="h-4 w-4 accent-[#C4B5A0]" data-testid="coupon-active-input" />
                <span className="text-sm font-montserrat text-[#2C2C2C]">{form.active ? 'Active' : 'Inactive'}</span>
              </label>
            </Field>
          </div>
          <Field label="Banner Text">
            <Input value={form.banner_text} onChange={(e) => set('banner_text', e.target.value)} placeholder="🎉 Get 10% OFF — Use Code: WELCOME10" data-testid="coupon-banner-input" />
          </Field>
          <Field label="Urgency Text">
            <Input value={form.urgency_text} onChange={(e) => set('urgency_text', e.target.value)} placeholder="Limited-time online launch offer" data-testid="coupon-urgency-input" />
          </Field>
          <div className="flex gap-2 mt-3">
            <Button onClick={save} disabled={saving} className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white" data-testid="coupon-save-btn">
              <Save className="h-4 w-4 mr-2" /> {creating ? 'Create' : 'Save'}
            </Button>
            <Button variant="outline" onClick={cancelEdit} className="border-[#8B7D6B] text-[#8B7D6B]">
              <X className="h-4 w-4 mr-2" /> Cancel
            </Button>
          </div>
        </Card>
      )}

      {loading ? (
        <p className="text-center text-sm text-[#8B7D6B]">Loading...</p>
      ) : coupons.length === 0 ? (
        <p className="text-center text-sm text-[#8B7D6B] py-8">No coupons yet — create your first one above.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-montserrat">
            <thead className="bg-[#F5F3F0]">
              <tr>
                <Th>Code</Th><Th>Discount</Th><Th>Min Order</Th><Th>Expiry</Th><Th>Status</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.code} className="border-b border-[#F5F3F0]" data-testid={`coupon-row-${c.code}`}>
                  <td className="p-3 font-seasons text-[#2C2C2C]">{c.code}</td>
                  <td className="p-3 text-[#2C2C2C]">{c.discount_percent}%</td>
                  <td className="p-3 text-[#2C2C2C]">₹{c.min_order_value || 0}</td>
                  <td className="p-3 text-[#8B7D6B]">{c.expiry_date ? c.expiry_date.slice(0, 10) : 'No expiry'}</td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {c.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(c)} className="text-[#C4B5A0]" data-testid={`edit-coupon-${c.code}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(c.code)} className="text-red-600 hover:bg-red-50" data-testid={`delete-coupon-${c.code}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

const Field = ({ label, children }) => (
  <div className="mt-2">
    <label className="block text-xs font-montserrat text-[#6B4E37] uppercase tracking-wider mb-1">{label}</label>
    {children}
  </div>
);

const Th = ({ children }) => (
  <th className="text-left p-3 font-montserrat text-xs text-[#6B4E37] uppercase tracking-wider">{children}</th>
);
